import { useTranslation } from "react-i18next";import React from 'react';
import { Users, Shield, Database, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';

export const Admin = () => {const { t } = useTranslation("ezparcel");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("adminPanel", "Admin Panel")}</h1>
        <p className="text-muted-foreground mt-2">{t("manageSystemSettings", "Manage system settings, users, roles, and permissions")}

        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">{t("totalUsers", "Total Users")}</p>
            </div>
            <p className="text-3xl font-bold">{t("124", "124")}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">{t("activeRoles", "Active Roles")}</p>
            </div>
            <p className="text-3xl font-bold">8</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">{t("dbSize", "DB Size")}</p>
            </div>
            <p className="text-3xl font-bold">{t("452Gb", "45.2 GB")}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">{t("systemHealth", "System Health")}</p>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <span className="text-lg font-bold">{t("healthy", "Healthy")}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">{t("userManagement", "User Management")}</TabsTrigger>
          <TabsTrigger value="roles">{t("rolesPermissions", "Roles & Permissions")}</TabsTrigger>
          <TabsTrigger value="system">{t("systemSettings", "System Settings")}</TabsTrigger>
          <TabsTrigger value="audit">{t("auditLogs", "Audit Logs")}</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>{t("userAccounts", "User Accounts")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {['Admin User', 'John Mitchell', 'Sarah Johnson', 'Michael Chen'].map((user, idx) =>
                <div key={idx} className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary)/.1)] flex items-center justify-center">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">{user}</p>
                        <p className="text-sm text-muted-foreground">
                          {user.toLowerCase().replace(' ', '.')}{t("ezparcelcom", "@ezparcel.com")}
                      </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge>{idx === 0 ? 'Admin' : idx === 1 ? 'Driver' : idx === 2 ? 'Dispatcher' : 'Driver'}</Badge>
                      <Button variant="outline" size="sm">{t("edit", "Edit")}</Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <Card>
            <CardHeader>
              <CardTitle>{t("rolePermissions", "Role Permissions")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                { role: 'Admin', permissions: 'Full System Access' },
                { role: 'Dispatcher', permissions: 'Routes, Shipments, Drivers' },
                { role: 'Driver', permissions: 'Assigned Routes, POD Capture' },
                { role: 'Warehouse Staff', permissions: 'Inventory, Scanning, Manifests' },
                { role: 'Customer Support', permissions: 'Shipments View, Customer Communication' },
                { role: 'Finance', permissions: 'Billing, Invoices, Reports' }].
                map((item, idx) =>
                <div key={idx} className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div>
                      <p className="font-medium">{item.role}</p>
                      <p className="text-sm text-muted-foreground mt-1">{item.permissions}</p>
                    </div>
                    <Button variant="outline" size="sm">{t("configure", "Configure")}</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle>{t("systemConfiguration", "System Configuration")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">{t("aiRouteOptimization", "AI Route Optimization")}</p>
                    <p className="text-sm text-muted-foreground">{t("useAiFor", "Use AI for intelligent route planning")}</p>
                  </div>
                  <Badge className="status-badge status-completed">{t("enabled", "Enabled")}</Badge>
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">{t("realtimeGpsTracking", "Real-time GPS Tracking")}</p>
                    <p className="text-sm text-muted-foreground">{t("liveVehicleLocation", "Live vehicle location updates")}</p>
                  </div>
                  <Badge className="status-badge status-completed">{t("enabled", "Enabled")}</Badge>
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">{t("automaticNotifications", "Automatic Notifications")}</p>
                    <p className="text-sm text-muted-foreground">{t("sendAlertsFor", "Send alerts for exceptions and updates")}</p>
                  </div>
                  <Badge className="status-badge status-completed">{t("enabled", "Enabled")}</Badge>
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">{t("multitenantSupport", "Multi-tenant Support")}</p>
                    <p className="text-sm text-muted-foreground">{t("enableMultipleCustomer", "Enable multiple customer accounts")}</p>
                  </div>
                  <Badge className="status-badge status-completed">{t("enabled", "Enabled")}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>{t("recentActivity", "Recent Activity")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                { user: 'Admin User', action: 'Updated system settings', time: '2 minutes ago' },
                { user: 'John Mitchell', action: 'Completed route ROUTE-2024-001', time: '15 minutes ago' },
                { user: 'Sarah Johnson', action: 'Created new shipment EZP-2024-X1Y2Z', time: '1 hour ago' },
                { user: 'Admin User', action: 'Added new driver Michael Chen', time: '3 hours ago' }].
                map((log, idx) =>
                <div key={idx} className="flex items-center gap-4 p-3 border border-border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{log.user}</p>
                      <p className="text-sm text-muted-foreground">{log.action}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{log.time}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};